import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaiementSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('loading');
  const sessionId = searchParams.get('session_id');
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    // If the session_id starts with 'pi_', it's a PaymentIntent (saved card payment)
    if (sessionId.startsWith('pi_')) {
      setStatus('success');
      localStorage.removeItem('ecoripeurs_form');
      return;
    }

    const pollStatus = async () => {
      if (pollCount.current >= 5) {
        setStatus('pending');
        return;
      }
      pollCount.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          localStorage.removeItem('ecoripeurs_form');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }
        // Keep polling
        setTimeout(pollStatus, 2000);
      } catch {
        setStatus('error');
      }
    };

    pollStatus();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-green-600 mx-auto mb-3" />
          <p className="text-gray-600">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center" data-testid="payment-result">
        {status === 'success' ? (
          <>
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Paiement confirmé</h1>
            <p className="text-gray-600 mb-6">Votre paiement a bien été reçu. Vous recevrez par email une confirmation de votre commande sous 2 heures.</p>
          </>
        ) : status === 'pending' ? (
          <>
            <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Paiement en cours</h1>
            <p className="text-gray-600 mb-6">Votre paiement est en cours de traitement. Vous recevrez une confirmation par email.</p>
          </>
        ) : (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h1>
            <p className="text-gray-600 mb-6">Le paiement n'a pas pu être vérifié. Contactez-nous si vous avez été débité.</p>
          </>
        )}
        <Button onClick={() => navigate(user ? '/dashboard' : '/')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="back-to-account-btn">
          {user ? 'Voir mes commandes' : "Retour à l’accueil"}
        </Button>
      </Card>
    </div>
  );
};

export default PaiementSuccess;
